import {
  CalendarRange,
  LayoutDashboard,
  LogOut,
  Moon,
  PackageSearch,
  Sun,
  UserCog,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { Button, cn } from '@proerp/ui';
import { logout } from '@/lib/auth';
import { toggleTheme, useTheme } from '@/lib/theme';
import { usePortalUser } from '@/lib/usePortalUser';

/** Ein Navigationspunkt des Portals — nur sichtbar, wenn freigeschaltet. */
export interface PortalNavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

/**
 * Welche Punkte darf dieser Zugang sehen? Rein für die Anzeige — geschützt
 * wird serverseitig (PortalGuard, ADR 0085).
 */
export function navItemsFor(access: {
  canSeeForecast: boolean;
  canSeeAssembly: boolean;
}): PortalNavItem[] {
  const items: PortalNavItem[] = [
    { to: '/', label: 'Übersicht', icon: <LayoutDashboard className="size-4" aria-hidden /> },
  ];
  if (access.canSeeForecast) {
    items.push({
      to: '/bedarfsvorschau',
      label: 'Bedarfsvorschau',
      icon: <PackageSearch className="size-4" aria-hidden />,
    });
  }
  if (access.canSeeAssembly) {
    items.push({
      to: '/montagen',
      label: 'Montagen',
      icon: <CalendarRange className="size-4" aria-hidden />,
    });
  }
  // Immer sichtbar: Der eigene Zugang hängt an keiner fachlichen Freischaltung
  // (ADR 0034/0085) — sonst könnte ein Partner ohne freigeschaltete Sicht seinen
  // zweiten Faktor nicht verwalten.
  items.push({
    to: '/einstellungen',
    label: 'Zugang',
    icon: <UserCog className="size-4" aria-hidden />,
  });
  return items;
}

/**
 * Rahmen des angemeldeten Portals (ADR 0085): Kopfzeile mit Partnername,
 * Navigation und Abmelden. Bewusst ohne Seitenleiste — das Portal hat drei
 * Seiten, eine Leiste wie im ERP wäre Ballast.
 *
 * Die Kopfzeile klebt oben (`sticky`): Auf einer langen Bedarfsvorschau ist der
 * Wechsel zur Montage-Sicht sonst nur nach einer Scroll-Reise erreichbar.
 *
 * Die Navigation steht **einmal** im Dokument und rutscht auf schmalen Geräten
 * per `flex-wrap` in eine eigene Zeile. Zwei Navigationen (eine für Desktop,
 * eine fürs Telefon) wären der bequemere Weg — und würden jeden Menüpunkt
 * doppelt in den Baum stellen, samt doppelter Ansage im Screenreader.
 *
 * **Mobil-Audit 2026-08-10:** Die Punkte waren auf 390 px zusammen breiter als
 * der Platz (403 px in 358 px) — der dritte war abgeschnitten und nur durch
 * seitliches Wischen erreichbar. Behoben, indem die Punkte auf dem Telefon
 * gestapelt werden (Symbol über Text) und sich die Zeile teilen.
 *
 * Eine Leiste am UNTEREN Rand (das übliche Muster fürs Telefon) wurde bewusst
 * nicht gebaut: Die Kopfzeile trägt `backdrop-blur`, und `backdrop-filter` macht
 * ein Element zum Bezugsrahmen für `position: fixed` — die Leiste klebte an der
 * Kopfzeile statt am Fensterrand. Sie unten zu verankern hieße, die Navigation
 * aus der Kopfzeile herauszuziehen und damit das Desktop-Layout umzubauen. Das
 * ist eine eigene Entscheidung, kein Nebeneffekt einer Mobil-Anpassung.
 */
export function PortalShell() {
  const user = usePortalUser();
  const items = user.data ? navItemsFor(user.data) : [];

  return (
    <div className="flex min-h-screen flex-col bg-muted/20">
      <header className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
          <div className="mr-auto flex min-w-0 items-center gap-3">
            <BrandMark />
            <div className="min-w-0">
              <p className="text-[0.7rem] font-medium uppercase tracking-wider text-muted-foreground">
                PROBIG Partnerportal
              </p>
              <p className="truncate text-sm font-semibold text-foreground">
                {user.data?.partnerName ?? '…'}
              </p>
            </div>
          </div>

          <div className="order-1 flex items-center gap-1">
            <ThemeToggle />
            <Button type="button" variant="ghost" size="sm" onClick={logout}>
              <LogOut className="size-4" aria-hidden />
              <span className="hidden sm:inline">Abmelden</span>
            </Button>
          </div>

          {/* order/basis: auf schmalen Geräten eine eigene Zeile, ab md wieder
              zwischen Name und „Abmelden". Kein `overflow-x-auto` mehr — die
              Punkte teilen sich die Zeile, es gibt nichts wegzuscrollen. */}
          <nav
            className="order-2 flex basis-full items-stretch gap-1 md:order-none md:basis-auto md:items-center"
            aria-label="Portal"
          >
            {items.map((item) => (
              <NavItem key={item.to} item={item} />
            ))}
          </nav>
        </div>
      </header>

      {/* Auf dem Telefon oben etwas knapper: Die Kopfzeile braucht dort zwei
          Zeilen, und py-8 schob den Seitentitel unter die Falz. */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-8 pt-5 sm:pt-8">
        <Outlet />
      </main>

      <footer className="border-t border-border px-4 py-4 text-center text-xs text-muted-foreground">
        Angemeldet als {user.data?.email ?? '—'}
      </footer>
    </div>
  );
}

/**
 * Hell/Dunkel. Voreinstellung ist die Systemeinstellung des Partners — wer sein
 * Gerät dunkel gestellt hat, bekommt keine weiße Wand. Der Knopf trägt eine
 * `aria-label`, weil er nur ein Symbol zeigt.
 */
function ThemeToggle() {
  const theme = useTheme();
  const isDark = theme === 'dark';
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={toggleTheme}
      aria-label={isDark ? 'Zur hellen Ansicht wechseln' : 'Zur dunklen Ansicht wechseln'}
      title={isDark ? 'Helle Ansicht' : 'Dunkle Ansicht'}
    >
      {isDark ? <Sun className="size-4" aria-hidden /> : <Moon className="size-4" aria-hidden />}
    </Button>
  );
}

function NavItem({ item }: { item: PortalNavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          // min-h-11 ≈ 44 px: die kleinste Fläche, die sich sicher treffen lässt.
          // Auf dem Telefon Symbol ÜBER dem Text und `flex-1` — so passen drei
          // Punkte nebeneinander, ohne dass etwas abgeschnitten wird oder man
          // wischen muss. Ab `sm` wieder eine Zeile mit Symbol links.
          'flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-1.5',
          'text-[0.6875rem] leading-tight transition-colors',
          'sm:min-h-11 sm:flex-none sm:flex-row sm:gap-2 sm:px-3 sm:py-0 sm:text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isActive
            ? 'bg-accent font-medium text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )
      }
    >
      {item.icon}
      {item.label}
    </NavLink>
  );
}

/**
 * Bildmarke: zwei versetzte Balken als Andeutung eines Behälters. Bewusst als
 * SVG und nicht als Emoji (UI-Regel: Emojis sind keine Symbole) und ohne
 * externes Bild — das Portal lädt nichts von fremden Hosts (CSP).
 */
function BrandMark() {
  return (
    <span
      className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-background"
      aria-hidden
    >
      <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M4 7h16M4 12h10M4 17h13" strokeLinecap="round" />
      </svg>
    </span>
  );
}
